import { create } from "zustand";
import { subscribeWithSelector } from "zustand/middleware";
import { immer } from "zustand/middleware/immer";
import {
  Component,
  Project,
  EditorState,
  Viewport,
  EditorPanel,
  Page,
} from "../../types";
import type { ColorScheme, ColorSchemeWriteTarget } from "../../types/color-scheme";
import { storage } from "../../utils";
import { config } from "../../config";
import { createGenerationSlice } from "./generation-slice";
import { createCanvasSlice, type CanvasSlice, canvasInitialState } from "./canvas-slice";
import { createComponentSlice, type ComponentSlice } from "./component-slice";
import { createModeSlice } from "./mode-slice";
import { createPendingContainerSlice } from "./pending-container-slice";
import { createPageSlice, type PageSlice } from "./page-slice";
import { createProjectSlice, type ProjectSlice } from "./project-slice";
import { createSketchSlice } from "./sketch-slice";
import { createTextSelectionSlice } from "./text-selection-slice";
import { createVersioningSlice } from "./versioning-slice";
import { createHistorySlice, type HistorySlice, historyInitialState } from "./history-slice";
import {
  cloneComponentSnapshot,
  collectComponentTreesForClone,
  createComponentClonePlan,
} from "./editor-store-helpers";
import type { GenerationSlice } from "./generation-slice";
import type { ModeSlice } from "./mode-slice";
import type { PendingContainerSlice } from "./pending-container-slice";
import type { SketchSlice } from "./sketch-slice";
import type { TextSelectionSlice } from "./text-selection-slice";
import type { VersioningSlice } from "./versioning-slice";
import type { CollaborationBridgeStore } from "./collaboration-bridge-types";
import { registerVersioningEditorSource } from "./versioning-editor-source";

export type { SketchRectangle, AnnotationData } from "./sketch-slice";
// Using existing versioning integration pattern
// import { useVersioningStore } from '../versioning/versioning-store';

let collaborationBridgeRef: CollaborationBridgeStore | null = null;

export function registerCollaborationBridge(
  bridge: CollaborationBridgeStore,
): void {
  collaborationBridgeRef = bridge;
}

const getCollaborationBridge = (): CollaborationBridgeStore | null =>
  collaborationBridgeRef;

export interface EditorStoreState extends EditorState,
  SketchSlice,
  GenerationSlice,
  ModeSlice,
  VersioningSlice,
  CanvasSlice,
  ComponentSlice,
  PageSlice,
  ProjectSlice,
  HistorySlice,
  PendingContainerSlice,
  TextSelectionSlice {
  // Additional state
  project: Project | null;
  components: Component[]; // Components for current page
  componentMap: Map<string, Component>; // Components for current page
  clipboard: Component[];
  isModified: boolean;
  lastSaved: Date | null;
  lastUpdateTimestamp: number; // Force re-renders when components change
  activeColorScheme?: ColorScheme;
  styleWriteTarget: ColorSchemeWriteTarget;

  // Multi-page state
  pages: Page[];
  pageMap: Map<string, Page>;
  pageComponents: Map<string, Component[]>; // Components by page ID
  pageHistoryMap: Map<
    string,
    { past: Component[][]; present: Component[]; future: Component[][] }
  >; // History per page
  currentPage: Page | null;
  currentPageId: string | undefined;

  // Page-aware component operations
  // Selection actions
  selectComponent: (id: string) => void;
  selectMultipleComponents: (ids: string[]) => void;
  clearSelection: () => void;
  toggleComponentSelection: (id: string) => void;
  deleteSelectedComponents: () => void;
  copySelectedComponents: () => void;
  getSelectionContext: () => Component[];
  selectAllComponents: () => void;
  // Smart selection management

  // Clipboard actions
  copyComponents: (ids: string[]) => void;
  cutComponents: (ids: string[]) => void;
  pasteComponents: () => void;

  // Viewport actions
  setViewport: (viewport: Viewport) => void;
  setZoom: (zoom: number) => void;
  zoomIn: () => void;
  zoomOut: () => void;
  resetZoom: () => void;

  // Editor state actions
  setHoveredComponent: (id: string | undefined) => void;
  setPreviewHighlightId: (id: string | undefined) => void;
  setActiveColorScheme: (scheme: ColorScheme | undefined) => void;
  setStyleWriteTarget: (target: ColorSchemeWriteTarget) => void;
  toggleGrid: () => void;
  toggleSnapToGrid: () => void;
  toggleGuides: () => void;
  togglePreviewMode: () => void;
  setActivePanel: (panel: EditorPanel) => void;

  // Canvas actions
  selectCanvas: () => void;

  // Utility actions
  isCanvasSelected: () => boolean;

  // Auto-save
  enableAutoSave: () => void;
  disableAutoSave: () => void;
}

export const useEditorStore = create<EditorStoreState>()(
  subscribeWithSelector(
    immer((set, get) => ({
      // Initial state - EditorState interface properties
      selectedComponents: [],
      hoveredComponent: undefined,
      previewHighlightId: undefined,
      viewport: Viewport.DESKTOP,
      zoom: config.editor.canvas.defaultZoom,
      gridEnabled: true,
      gridSize: 20,
      snapToGrid: true,
      showGuides: true,
      isPreviewMode: false,
      activePanel: EditorPanel.COMPONENTS,
      canvasSelected: false,

      // Multi-page EditorState interface properties
      currentPageId: undefined,
      pageHistory: [],
      crossPageClipboard: [],
      navigationVisible: true,
      pageSearchQuery: undefined,

      // Additional EditorStoreState properties
      project: null,
      components: [],
      componentMap: new Map(),
      ...canvasInitialState,
      ...historyInitialState,
      clipboard: [],
      isModified: false,
      lastSaved: null,
      lastUpdateTimestamp: 0,
      activeColorScheme: undefined,
      styleWriteTarget: "both",
      pages: [],
      pageMap: new Map(),
      pageComponents: new Map(),
      pageHistoryMap: new Map(),
      currentPage: null,

      ...createSketchSlice<EditorStoreState>(set, get),
      ...createGenerationSlice<EditorStoreState>(set, get),
      ...createModeSlice<EditorStoreState>(set, get),
      ...createVersioningSlice<EditorStoreState>(set, get),
      ...createCanvasSlice<EditorStoreState>(set, get),
      ...createHistorySlice<EditorStoreState>(set, get),
      ...createComponentSlice<EditorStoreState>(set, get, getCollaborationBridge),
      ...createPageSlice<EditorStoreState>(set, get),
      ...createProjectSlice<EditorStoreState>(set, get),
      ...createPendingContainerSlice<EditorStoreState>(set, get),
      ...createTextSelectionSlice<EditorStoreState>(set, get),

      // Selection actions
      selectComponent: (id: string) => {
        set((state) => {
          state.selectedComponents = [id];
          state.canvasSelected = false;
        });

        // Send collaboration selection update
        try {
          const useCollaborationEditorBridge = getCollaborationBridge();
          if (!useCollaborationEditorBridge) return; // Collaboration bridge not available
          const bridge = useCollaborationEditorBridge.getState();
          if (bridge.isCollaborationActive) {
            bridge.sendSelectionUpdate([id]);
          }
        } catch {
          // Collaboration bridge not available, continue
        }
      },

      selectMultipleComponents: (ids: string[]) => {
        set((state) => {
          state.selectedComponents = ids;
          state.canvasSelected = false;
        });

        // Send collaboration selection update
        try {
          const useCollaborationEditorBridge = getCollaborationBridge();
          if (!useCollaborationEditorBridge) return; // Collaboration bridge not available
          const bridge = useCollaborationEditorBridge.getState();
          if (bridge.isCollaborationActive) {
            bridge.sendSelectionUpdate(ids);
          }
        } catch {
          // Collaboration bridge not available, continue
        }
      },

      clearSelection: () => {
        set((state) => {
          state.selectedComponents = [];
          state.canvasSelected = false;
        });

        // Send collaboration selection update
        try {
          const useCollaborationEditorBridge = getCollaborationBridge();
          if (!useCollaborationEditorBridge) return; // Collaboration bridge not available
          const bridge = useCollaborationEditorBridge.getState();
          if (bridge.isCollaborationActive) {
            bridge.sendSelectionUpdate([]);
          }
        } catch {
          // Collaboration bridge not available, continue
        }
      },

      toggleComponentSelection: (id: string) => {
        set((state) => {
          const wasSelected = state.selectedComponents.includes(id);
          if (wasSelected) {
            state.selectedComponents = state.selectedComponents.filter(
              (selectedId) => selectedId !== id,
            );
          } else {
            state.selectedComponents.push(id);
          }
        });
      },

      /**
       * Delete all currently selected components
       * Week 3: Selection System implementation
       */
      deleteSelectedComponents: () => {
        const { selectedComponents } = get();
        if (selectedComponents.length === 0) return;

        // Delete each selected component
        selectedComponents.forEach((id) => {
          get().deleteComponent(id);
        });

        // Clear selection after deletion
        get().clearSelection();
      },

      /**
       * Copy selected components to clipboard
       * Week 3: Selection System implementation
       */
      copySelectedComponents: () => {
        const context = get().getSelectionContext();

        if (context.length === 0) {
          console.warn('[SELECTION] No components selected to copy');
          return;
        }

        try {
          // Serialize component data for clipboard
          const serialized = JSON.stringify(context, null, 2);

          // Try to use modern clipboard API
          if (navigator.clipboard && navigator.clipboard.writeText) {
            navigator.clipboard.writeText(serialized).catch((error) => {
              console.error('[SELECTION] Failed to copy to clipboard:', error);
            });
          }
          // Silent fallback for older browsers - data is already serialized
        } catch (error) {
          console.error('[SELECTION] Error copying components:', error);
        }
      },

      /**
       * Get full component details for all selected components
       * Used for AI command context
       * Week 3: Selection System implementation
       */
      getSelectionContext: () => {
        const { selectedComponents, components } = get();

        return selectedComponents
          .map((id) => components.find((c) => c.id === id))
          .filter((component): component is Component => component !== undefined);
      },

      /**
       * Select all components on the current page
       * Week 3: Selection System implementation
       */
      selectAllComponents: () => {
        const { components } = get();

        // Select all components (in single-page mode this is all components)
        const allComponentIds = components.map((c) => c.id);

        set((state) => {
          state.selectedComponents = allComponentIds;
          state.canvasSelected = false;
        });

        // Send collaboration selection update
        try {
          const useCollaborationEditorBridge = getCollaborationBridge();
          if (!useCollaborationEditorBridge) return;
          const bridge = useCollaborationEditorBridge.getState();
          if (bridge.isCollaborationActive) {
            bridge.sendSelectionUpdate(allComponentIds);
          }
        } catch {
          // Collaboration bridge not available, continue
        }
      },

      /**
       * Manually rebuild component hierarchy from children arrays
       * Useful when components are loaded with missing parentId fields
       */
      // Clipboard actions
      copyComponents: (ids: string[]) => {
        const components = collectComponentTreesForClone(
          ids,
          get().components,
        ).map(cloneComponentSnapshot);

        set((state) => {
          state.clipboard = components;
        });
      },

      cutComponents: (ids: string[]) => {
        get().copyComponents(ids);
        ids.forEach((id) => get().deleteComponent(id));
      },

      pasteComponents: () => {
        const { clipboard } = get();
        if (clipboard.length === 0) {
          return;
        }

        const pastePlan = createComponentClonePlan(clipboard, {
          positionOffset: {
            x: 20,
            y: 20,
          },
        });

        pastePlan.components.forEach((component) => {
          get().addComponent(component);
        });
      },

      // Viewport actions
      setViewport: (viewport: Viewport) => {
        set((state) => {
          state.viewport = viewport;
        });
      },

      setZoom: (zoom: number) => {
        const clampedZoom = Math.max(
          config.editor.canvas.minZoom,
          Math.min(config.editor.canvas.maxZoom, zoom),
        );

        set((state) => {
          state.zoom = clampedZoom;
        });
      },

      zoomIn: () => {
        const { zoom } = get();
        get().setZoom(zoom + config.editor.canvas.zoomStep);
      },

      zoomOut: () => {
        const { zoom } = get();
        get().setZoom(zoom - config.editor.canvas.zoomStep);
      },

      resetZoom: () => {
        get().setZoom(config.editor.canvas.defaultZoom);
      },

      // Editor state actions
      setHoveredComponent: (id: string | undefined) => {
        set((state) => {
          state.hoveredComponent = id;
        });
      },

      setPreviewHighlightId: (id: string | undefined) => {
        set((state) => {
          state.previewHighlightId = id;
        });
      },

      setActiveColorScheme: (scheme: ColorScheme | undefined) => {
        set((state) => {
          state.activeColorScheme = scheme;
        });
      },

      setStyleWriteTarget: (target: ColorSchemeWriteTarget) => {
        set((state) => {
          state.styleWriteTarget = target;
        });
      },

      toggleGrid: () => {
        set((state) => {
          state.gridEnabled = !state.gridEnabled;
          state.canvasSettings.grid.enabled = state.gridEnabled;
        });
      },

      toggleSnapToGrid: () => {
        set((state) => {
          state.snapToGrid = !state.snapToGrid;
          state.canvasSettings.grid.snapToGrid = state.snapToGrid;
        });
      },

      toggleGuides: () => {
        set((state) => {
          state.showGuides = !state.showGuides;
        });
      },

      togglePreviewMode: () => {
        set((state) => {
          state.isPreviewMode = !state.isPreviewMode;
        });
      },

      setActivePanel: (panel: EditorPanel) => {
        set((state) => {
          state.activePanel = panel;
        });
      },

      // Canvas actions
      selectCanvas: () => {
        set((state) => {
          state.selectedComponents = [];
          state.selectedSketchId = null; // Clear sketch selection when canvas is selected
          state.canvasSelected = true;
          state.activePanel = EditorPanel.PROPERTIES;
        });
      },

      // Utility functions
      isCanvasSelected: () => {
        return get().canvasSelected;
      },

      // Auto-save
      enableAutoSave: () => {
        // Implementation would set up interval for auto-saving
      },

      disableAutoSave: () => {
        // Implementation would clear auto-save interval
      },

    })),
  ),
);

registerVersioningEditorSource(() => {
  const state = useEditorStore.getState();
  return {
    pages: state.pages,
    pageComponents: state.pageComponents,
    projectId: state.project?.id ?? null,
  };
});

// Selectors for common use cases
export const useEditorState = () => {
  const {
    viewport,
    zoom,
    gridEnabled,
    snapToGrid,
    showGuides,
    isPreviewMode,
    activePanel,
  } = useEditorStore();

  return {
    viewport,
    zoom,
    gridEnabled,
    snapToGrid,
    showGuides,
    isPreviewMode,
    activePanel,
  };
};

export const useEditorSelection = () => {
  const { selectedComponents, hoveredComponent } =
    useEditorStore();
  return { selectedComponents, hoveredComponent };
};

export const useEditorHistory = () => {
  const { canUndo, canRedo, undo, redo } = useEditorStore();
  return { canUndo: canUndo(), canRedo: canRedo(), undo, redo };
};

// Subscribe to changes for persistence
useEditorStore.subscribe(
  (state) => ({
    viewport: state.viewport,
    zoom: state.zoom,
    gridEnabled: state.gridEnabled,
    snapToGrid: state.snapToGrid,
    showGuides: state.showGuides,
    activePanel: state.activePanel,
    canvasSettings: state.canvasSettings,
  }),
  (editorState) => {
    // Save editor preferences to local storage
    storage.set(config.storage.keys.editorState, editorState);
  },
);
